import React, { useEffect, useMemo, useState } from "react";
import "leaflet/dist/leaflet.css";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import api from "../../services/api";
import { endpoints } from "../../services/endpoints";
import { resolveBySerial, getPermissionsWithUsers } from "../../utils/padlock";

import { fmtDate, fmtBattery, fmtCoords, isNum } from "../../utils/format";

import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  useMap,
} from "react-leaflet";
import L from "leaflet";

// --- componente auxiliar para mover el mapa ---
function MapMover({ center }) {
  const map = useMap();

  useEffect(() => {
    if (center) {
      map.flyTo(center, 12);
    }
  }, [center, map]);

  return null;
}

// -- Componente para las fechas --
function parseDateTimeInput(value, isEnd = false) {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d)) return null;

  // datetime-local usually lacks seconds (YYYY-MM-DDTHH:mm)
  const timePart = (value.split("T")[1] || "").trim();
  const hasSeconds = timePart.split(":").length >= 3;
  if (!hasSeconds) {
    if (isEnd) d.setSeconds(59, 999);
    else d.setSeconds(0, 0);
  }

  return d;
}

function filterByDate(arr, fromDateTime, toDateTime) {
  const from = parseDateTimeInput(fromDateTime, false);
  const to = parseDateTimeInput(toDateTime, true);

  return arr.filter((e) => {
    const t = new Date(e.capturedAt || e.openedAt);
    if (!t || isNaN(t)) return false;

    if (from && t < from) return false;
    if (to && t > to) return false;

    return true;
  });
}

function numberedIcon(number) {
  return L.divIcon({
    html: `<div style="
        background:white;
        border:2px solid blue;
        border-radius:50%;
        width:24px;
        height:24px;
        display:flex;
        align-items:center;
        justify-content:center;
        font-size:12px;
        font-weight:bold;
        color:blue;
    ">${number}</div>`,
    className: "", 
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
}

export default function DashboardPadlockIDSuper() {
  const { serialNumber } = useParams();
  const navigate = useNavigate();

  const [padlockId, setPadlockId] = useState(null);
  const [data, setData] = useState(null);

  const [track, setTrack] = useState([]);
  const [historicRoute, setHistoricRoute] = useState([]);
  const [basicLocationRows, setBasicLocationRows] = useState([]);

  const [resolving, setResolving] = useState(true);
  const [loadingDash, setLoadingDash] = useState(true);
  const [err, setErr] = useState(null);

  // --- permisos ---
  const [perms, setPerms] = useState([]);
  const [permsLoading, setPermsLoading] = useState(false);
  const [permsErr, setPermsErr] = useState(null);

  // --- aperturas reales desde openEvent ---
  const [openEvents, setOpenEvents] = useState([]);
    // --- estado para cuando seleccionas coordenadas desde la tabla ---
  const [selectedCoords, setSelectedCoords] = useState(null);

  // --- paginación ---
  const [pageTable, setPageTable] = useState(1);
  const pageSize = 10;

  // --- filtros de tabla ---
  const [tableMode, setTableMode] = useState("all");
  const [fromDateTime, setFromDateTime] = useState("");
  const [toDateTime, setToDateTime] = useState("");

  // Switch con ORS - sin ORS
  const [showORS, setShowORS] = useState(false);
  const [orsRoute, setOrsRoute] = useState([]);

  const [showLegend, setShowLegend] = useState(false);


  /////////////////////////////////////////////////////////
  /////////////////////// LOGICA /////////////////////////
  ////////////////////////////////////////////////////////

  // 1. Resolver ID desde serialNumber
  useEffect(() => {
    let alive = true;
    async function resolveId() {
      setResolving(true);
      setErr(null);
      try {
        const found = await resolveBySerial(serialNumber);
        if (!alive) return;
        if (found?.id) setPadlockId(found.id);
        else
          setErr(new Error("No se pudo resolver el candado por serialNumber."));
      } catch (e) {
        if (alive) setErr(e);
      } finally {
        if (alive) setResolving(false);
      }
    }
    if (serialNumber) resolveId();
    else {
      setErr(new Error("Falta el serialNumber en la URL."));
      setResolving(false);
    }
    return () => {
      alive = false;
    };
  }, [serialNumber]);

  // 2. Cargar dashboard 
  useEffect(() => {
    if(!padlockId) return;
    let alive = true;
    async function fetchDashboard(id) {
      setLoadingDash(true);
      setErr(null);
      try {
        const res = await api.get(endpoints.padlock.dashboard(id));
        const payload = res.data?.data ?? res.data;
        if (alive) setData(payload);
      } catch (e) {
        if (alive) setErr(e);
      } finally {
        if (alive) setLoadingDash(false);
      }
    }
    fetchDashboard(padlockId);
    return() => (alive = false);
  }, [padlockId]);

  // 3. Cargar permisos
  useEffect(() => {
    if(!padlockId) return;
    let alive = true;
    async function fetchPerms(id) {
      setPermsLoading(true);
      try {
        const arr = await getPermissionsWithUsers(id);
        if (alive) setPerms(arr);
      } catch (e) {
        if (alive) setPermsErr(e);
      } finally {
        if (alive) setPermsLoading(false);
      }
    }
    fetchPerms(padlockId);
    return () => (alive = false);
  }, [padlockId]);

  // 4. Ruta básica para tabla
  useEffect(() => {
    if(!padlockId) return;
    let alive = true;

    async function fetchBasicRoute(id) {
      try {
        const res = await api.get(endpoints.padlock.routeBasic(id));
        const arr = res.data?.data ?? res.data ?? [];

        if (!alive) return;

        const rows = arr
          .filter(e => isNum(e.latitude) && isNum(e.longitude))
          .map(e => ({
            id: `basic-${e.capturedAt}`,
            capturedAt: e.capturedAt,
            uploadedAt: e.uploadedAt,
            eventType: "Localización",
            transport: e.transport ?? "—",
            user: "—",
            lat: Number(e.latitude),
            lng: Number(e.longitude),
            battery:
              e.batteryPct != null
                ? Number(e.batteryPct)
                : "-",
            detail: e.motion_status ?? null,
          }));
        setBasicLocationRows(rows);
      } catch (e) {
        console.error("Error cargando ruta básica", e);
      }
    }
    fetchBasicRoute(padlockId);
    return () => (alive = false);
  }, [padlockId]);

  // 5. Ruta historica completa
  useEffect(() => {
    if(!padlockId) return;
    let alive = true;
    async function fetchRoute(id) {
      try {
        const res = await api.get(endpoints.padlock.route(id));
        const arr = res.data?.data ?? res.data ?? [];
        if (!alive) return;

        const pts = arr
          .filter((e) => isNum(e.latitude) && isNum(e.longitude))
          .map((e) => ({
            lat: Number(e.latitude),
            lng: Number(e.longitude),
            capturedAt: e.capturedAt,
            transport: e.transport ?? null,
            motion_status: e.motion_status ?? null,
            location: e.location ?? null,
          }));
        setHistoricRoute(pts);
      } catch (e) {
        console.error("Error cargando ruta histórica", e);
      }
    }
    fetchRoute(padlockId);
    return () => (alive = false);
  }, [padlockId]);

  // 6. Ruta reciente
  useEffect(() => {
    const nh = (data?.recent?.notehub || []).filter(
      (e) => isNum(e?.latitude) && isNum(e?.longitude)
    );

    if (nh.length > 0) {
      const pts = nh
        .map((e) => ({
          lat: Number(e.latitude),
          lng: Number(e.longitude),
          at: e.eventAt ?? null,
          speed: e.speed ?? null,
        }))
        .sort((a, b) => new Date(a.at) - new Date(b.at));
      setTrack(pts);
    } else if (
      data?.lastLocation &&
      isNum(data.lastLocation.latitude) &&
      isNum(data.lastLocation.longitude)
    ) {
      setTrack([
        {
          lat: Number(data.lastLocation.latitude),
          lng: Number(data.lastLocation.longitude),
          at: data.lastLocation.eventAt ?? null,
          speed: data.lastLocation.speed ?? null,
        },
      ]);
    } else {
      setTrack([]);
    }
  }, [data?.recent?.notehub, data?.lastLocation]);

  // 7. Cargar aperturas reales
  useEffect(() => {
    if(!padlockId) return;
    let alive = true;
    async function fetchOpens(id) {
      try {
        const res = await api.get(endpoints.padlock.openEvents(id));
        if (alive) setOpenEvents(res.data ?? []);
      } catch (e) {
        console.error("Error cargando aperturas", e);
      }
    }
    fetchOpens(padlockId);
    return () => (alive = false);
  }, [padlockId]);

  // 8. Ruta ORS
  useEffect(() => {
    if (!showORS || !padlockId) return;

    async function loadORS() {
      try {
        const res = await api.get(endpoints.padlock.orsRoute(padlockId));
        setOrsRoute(res.data?.route ?? []);
      } catch (err) {
        console.error("Error cargando ORS:", err);
      }
    }

    loadORS();
  }, [showORS, padlockId]);


  /////////////////////////////////////////
  ///////////// Derivados ////////////////
  ////////////////////////////////////////
  const padlock = data?.padlock;
  const lastTelemetry = data?.lastTelemetry;
  const lastLocation = data?.lastLocation;
  const metrics = data?.metrics;

  // Movimiento
  const inMotion =
    lastTelemetry?.inMotion === true ||
    (typeof lastLocation?.speed === "number" && lastLocation.speed > 0) ||
    padlock?.status === "MOVING";

  const motionLabel = (motion) => {
    const raw = String(motion || "").toLowerCase();
    if (raw.includes("stop")) return "Parado";
    if (raw.length > 0) return "En movimiento";
    return "—";
  };

  // Markers de apertura
  const openMarkers = useMemo(() => {
    return openEvents
      .filter((o) => isNum(o?.latitude) && isNum(o?.longitude))
      .map((o) => ({
        id: o.id,
        lat: Number(o.latitude),
        lng: Number(o.longitude),
        at: o.openedAt,
        by: o.author ?? "—",
      }));
  }, [openEvents]);

  const filteredOpenMarkers = useMemo(() => {
    const from = parseDateTimeInput(fromDateTime, false);
    const to = parseDateTimeInput(toDateTime, true);

    return openMarkers.filter((o) => {
      const t = new Date(o.at);
      if (!t || isNaN(t)) return false;
      if (from && t < from) return false;
      if (to && t > to) return false;
      return true;
    });
  }, [openMarkers, fromDateTime, toDateTime]);

  // Ultima apertura filtrada
  const lastOpenMarker = useMemo(() => {
    if (!filteredOpenMarkers.length) return null;
    return filteredOpenMarkers.reduce((latest, curr) =>
      new Date(curr.at) > new Date(latest.at) ? curr : latest
    );
  }, [filteredOpenMarkers]);

  // Fila de aperturas para la tabla
  const openRows = useMemo(
    () =>
      openEvents.map((ev) => ({
        id: `open-${ev.id}`,
        capturedAt: ev.openedAt ?? null,
        uploadedAt: ev.createdAt ?? null,
        eventType: "Apertura",
        detail: motionLabel(ev.motion_status ?? ev.motion),
        transport: ev.transport ?? null,
        user: ev.author ?? "—",
        lat: isNum(ev.latitude) ? Number(ev.latitude) : null,
        lng: isNum(ev.longitude) ? Number(ev.longitude) : null,
        battery:
          ev.batteryPct != null
            ? Number(ev.batteryPct)
            : "-",
      })),
    [openEvents]
  );

  const locationRows = useMemo(() => basicLocationRows, [basicLocationRows]);

  // Filtrar la tabla
  const tableRows = useMemo(() => {
    const rows = [];

    if (tableMode !== "coords") rows.push(...openRows);
    if (tableMode !== "apertures") rows.push(...locationRows);

    const filtered = filterByDate(rows, fromDateTime, toDateTime);

    const sorted = filtered.sort(
      (a, b) => new Date(b.capturedAt || 0) - new Date(a.capturedAt || 0)
    );

    const total = sorted.length;

    return sorted.map((row, idx) => ({
      ...row,
      index: total - idx,
    }));
  }, [locationRows, openRows, tableMode, fromDateTime, toDateTime]);

  // Paginacion
  const paginatedRows =
    tableRows.slice(
      (pageTable - 1) * pageSize,
      pageTable * pageSize);

  const totalTablePages = Math.ceil(tableRows.length / pageSize);

  // Reset page al cambiar modo
  useEffect(() => {
    setPageTable(1);
  }, [tableMode]);

  // --- última localización calculada ---
  const ultimaLocalizacion = useMemo(() => {
    // 1. Si existe última localización en la telemetría -> siempre prioridad
    if (
      data?.lastLocation &&
      isNum(data.lastLocation.latitude) &&
      isNum(data.lastLocation.longitude)
    ) {
      return [
        Number(data.lastLocation.latitude),
        Number(data.lastLocation.longitude),
      ];
    }

    // 2. Si hay track reciente
    if (track.length > 0) {
      const last = track[track.length - 1];
      return [last.lat, last.lng];
    }

    // 3. Si hay aperturas
    if (openEvents.length > 0) {
      const last = openEvents[0]; // vienen ordenados desc
      return [Number(last.latitude), Number(last.longitude)];
    }

    // 4. Fallback Madrid
    return [40.4167, -3.70325];
  }, [data?.lastLocation, track, openEvents]);

  const isLoading = resolving || loadingDash;

  const EVENT_COLORS = {
    Apertura: {
      row: "bg-red-50 hover:bg-red-100",
      button: "border-red-500 text-red-700",
      buttonActive: "bg-red-400 text-white border-red-500",
    },
    Localización: {
      row: "bg-blue-50 hover:bg-blue-100",
      button: "border-blue-500 text-blue-700",
      buttonActive: "bg-blue-400 text-white border-blue-500",
    },
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">
          Dispositivo #{padlock?.id ?? "—"} — {padlock?.name ?? "—"}
        </h1>
        <div className="flex gap-2">
          <button
            className="px-3 py-2 rounded border text-blue-600"
            onClick={() =>
              navigate(`/padlocks/${encodeURIComponent(serialNumber)}/permissions`)
            }
            disabled={!padlock?.id}
          >
            Dar permisos
          </button>
          <button className="px-3 py-2 rounded border" onClick={() => navigate(-1)}>
            Volver
          </button>
        </div>
      </div>

      {isLoading && <div className="text-gray-500">Cargando…</div>}

      {err && (
        <div className="text-red-600">
          <p className="font-semibold">Error</p>
          <pre className="whitespace-pre-wrap text-sm">{String(err)}</pre>
        </div>
      )}

      {!isLoading && !err && data && (
        <>
          {/* Tarjetas resumen */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Estado */}
            <div className="bg-white p-4 rounded shadow">
              <div className="text-sm text-gray-500">Estado</div>
              <div
                className={`text-lg font-semibold ${
                  inMotion
                    ? "text-blue-600"
                    : padlock?.status === "ACTIVE"
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              >
                {inMotion ? "En movimiento" : padlock?.status ?? "—"}
              </div>
              <div className="text-xs text-gray-400 mt-1">
                Serial: {padlock?.serialNumber ?? serialNumber}
              </div>
              <div className="text-xs text-gray-400">
                Compañía: {padlock?.company?.name ?? "—"}
              </div>
            </div>

            {/* Batería */}
            <div className="bg-white p-4 rounded shadow">
              <div className="text-sm text-gray-500">Batería</div>
              <div className="text-lg font-semibold">
                {lastTelemetry?.batteryPct != null
                  ? fmtBattery(lastTelemetry.batteryPct)
                  : "—"}
              </div>
              <div className="text-xs text-gray-400">
                Última sync: {fmtDate(lastTelemetry?.lastSyncAt)}
              </div>
            </div>

            {/* Última posición */}
            <div className="bg-white p-4 rounded shadow">
              <div className="text-sm text-gray-500">Última posición</div>
              <div className="text-lg font-semibold">
                {lastLocation?.location ?? "—"}
              </div>
              <div className="text-xs text-gray-400">
                {fmtCoords(lastLocation?.latitude, lastLocation?.longitude)}
              </div>
              <div className="text-xs text-gray-400">
                {fmtDate(lastLocation?.createdAt ?? lastTelemetry?.lastSyncAt)}
              </div>
            </div>

            {/* Actividad */}
            <div className="bg-white p-4 rounded shadow">
              <div className="text-sm text-gray-500">Actividad</div>
              <div className="text-lg font-semibold">
                Aperturas: {openEvents.length ?? 0}
              </div>
              <div className="text-xs text-gray-400">
                Telemetrías: {metrics?.totalTelemetries ?? 0}
              </div>
            </div>
          </div>

          {/* Mapa */}
          <div className="bg-white p-4 rounded shadow">
            <div className="flex items-center gap-2 mb-2 relative">
              <h3 className="font-semibold">Mapa</h3>

              {/* Botón "i" */}
              <button
                onClick={() => setShowLegend((v) => !v)}
                className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 text-gray-600 text-xs hover:bg-gray-300"
                title="Mostrar leyenda"
              >
                i
              </button>

              {/* Popup leyenda */}
              {showLegend && (
                <div className="absolute top-6 left-0 bg-white shadow-lg rounded-md p-3 text-xs z-50 w-48">
                  <p className="font-semibold text-gray-700 mb-2">Leyenda</p>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-3 h-3 rounded-full bg-blue-700 inline-block" />
                    Ruta reciente
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-3 h-3 rounded-full bg-blue-600 inline-block" />
                    Última apertura
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-3 h-3 rounded-full bg-red-500 inline-block" />
                    Apertura
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-3 h-3 rounded-full bg-yellow-400 inline-block" />
                    Última ubicación
                  </div>

                </div>
              )}
            </div>

            
            <MapContainer
              center={ultimaLocalizacion}
              zoom={12}
              style={{ height: "400px", width: "100%" }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="&copy; OpenStreetMap contributors"
              />

              {/* este mueve el mapa cuando seleccionas coords desde la tabla */}
              <MapMover center={selectedCoords || ultimaLocalizacion} />
 
              {/**Ruta */}
              {paginatedRows.filter(r => isNum(r.lat) && isNum(r.lng)).length > 1 && (
                <Polyline
                  positions={paginatedRows
                    .filter(r => isNum(r.lat) && isNum(r.lng))
                    .map(r => [Number(r.lat), Number(r.lng)])}
                  color="blue"
                  weight={3}
                  opacity={0.8}
                />
              )}


              {/* Puntos históricos numerados */}
              {tableRows.map((p) =>
                isNum(p.lat) && isNum(p.lng) ? (
                  <Marker
                    key={`row-${p.index}`}
                    position={[Number(p.lat), Number(p.lng)]}
                    icon={numberedIcon(p.index)}
                  >
                    <Popup>
                      <div className="bg-white p-2 text-sm leading-relaxed">

                        <div className="font-semibold text-base mb-1">
                          Punto #{p.index} — {p.eventType}
                        </div>

                        <div>
                          Hora: <span className="font-medium">
                            {fmtDate(p.capturedAt)}
                          </span>
                        </div>

                        <div>
                          Coordenadas:
                          <span className="font-medium">
                            {fmtCoords(p.lat, p.lng)}
                          </span>
                        </div>

                        {p.transport && (
                          <div>
                            Transporte: <span className="font-medium">{p.transport}</span>
                          </div>
                        )}

                        {p.user && (
                          <div>
                            Usuario: <span className="font-medium">{p.user}</span>
                          </div>
                        )}

                      </div>
                    </Popup>
                  </Marker>
                ) : null
              )}

              {/* Aperturas */}
              {filteredOpenMarkers.map((o) => (
                <Marker
                  key={`open-${o.id}`}
                  position={[o.lat, o.lng]}
                  icon={L.icon({
                    iconUrl: o.auth
                      ? "https://maps.gstatic.com/mapfiles/ms2/micons/green-dot.png"
                      : "https://maps.gstatic.com/mapfiles/ms2/micons/red-dot.png",
                    iconSize: [32, 32],
                  })}
                >
                  <Popup>
                    <div>
                      <strong>Apertura</strong>
                      <br />
                      {fmtDate(lastOpenMarker.at)}
                      <br />
                      <strong>Usuario: </strong>
                      {lastOpenMarker.by}
                    </div>
                  </Popup>
                </Marker>
              ))}

              {/* Última apertura destacada */}
              {lastOpenMarker && (
                <Marker
                  position={[lastOpenMarker.lat, lastOpenMarker.lng]}
                  icon={L.icon({
                    iconUrl:
                      "https://maps.gstatic.com/mapfiles/ms2/micons/blue-dot.png",
                    iconSize: [36, 36],
                  })}
                >
                  <Popup>
                    <div>
                      <strong>Última apertura</strong>
                      <br />
                      {fmtDate(lastOpenMarker.at)}
                      <br /> <strong>Usuario: </strong>
                      {lastOpenMarker.by}
                    </div>
                  </Popup>
                </Marker>
              )}

              {/* Ruta de track reciente */}
              {track.length >= 1 && (
                <Polyline
                  positions={track.map((p) => [p.lat, p.lng])}
                  color="blue"
                  weight={3}
                  opacity={0.6}
                />
              )}
              {/* Última localización conocida */}
              {lastLocation &&
                isNum(lastLocation.latitude) &&
                isNum(lastLocation.longitude) && (
                  <Marker
                    position={[Number(lastLocation.latitude), Number(lastLocation.longitude)]}
                    icon={L.icon({
                      iconUrl: "https://maps.gstatic.com/mapfiles/ms2/micons/yellow-dot.png",
                      iconSize: [32, 32],
                    })}
                  >
                    <Popup>
                      <div className="bg-white p-4 text-sm leading-relaxed">
                        <div className="font-semibold text-base mb-2">
                          {padlock?.serialNumber ?? "—"}{" "}
                        </div>

                        {/* Estado de movimiento */}
                        <div className="flex items-center gap-2 mb-1">
                          {String(
                            lastTelemetry?.motion ||
                            lastLocation?.motion_status ||
                            ""
                          ).toLowerCase().includes("stopped") ? (
                            <>
                              <span role="img" aria-label="parado">🟥</span>
                              <span>Parado</span>
                            </>
                          ) : (
                            <>
                              <span role="img" aria-label="en movimiento">🟩</span>
                              <span>En movimiento</span>
                            </>
                          )}
                        </div>

                        {/* Coordenadas */}
                        <div>
                          Coordenadas:{" "}
                          <span className="font-medium">
                            {fmtCoords(lastLocation?.latitude, lastLocation?.longitude)}
                          </span>
                        </div>

                        {/* Último evento */}
                        <div>
                          Último evento:{" "}
                          <span className="font-medium">{fmtDate(lastLocation?.eventAt)}</span>
                        </div>

                        {/* Batería */}
                        <div>
                          Batería:{" "}
                          <span className="font-medium">
                            {lastTelemetry?.batteryPct != null ? `${lastTelemetry.batteryPct}%` : "—"}
                          </span>
                        </div>

                        {/* Última sync */}
                        <div>
                          Última sync:{" "}
                          <span className="font-medium">{fmtDate(lastTelemetry?.lastSyncAt)}</span>
                        </div>

                        {/* Ubicación */}
                        <div>
                          Ubicación:{" "}
                          <span className="font-medium">
                            {lastLocation?.location || "—"}
                          </span>
                        </div>

                        {/* Transporte */}
                        {(lastTelemetry?.transport || lastLocation?.transport) && (
                        <div>
                          Transporte:{" "}
                          <span className="font-medium flex items-center gap-2">
                            {String(lastTelemetry?.transport || lastLocation?.transport)
                              .toLowerCase()
                              .includes("cell") ? (
                              <>
                                <span role="img" aria-label="celular">
                                  📱
                                </span>
                                <span>Celular (LTE)</span>
                              </>
                            ) : String(lastTelemetry?.transport || lastLocation?.transport)
                                .toLowerCase()
                                .includes("skylo") ? (
                              <>
                                <span role="img" aria-label="satelite">
                                  🛰️
                                </span>
                                <span>Satélite (Skylo)</span>
                              </>
                            ) : (
                              <>
                                <span role="img" aria-label="desconocido">
                                  ❔
                                </span>
                                <span>{lastTelemetry?.transport || lastLocation?.transport}</span>
                              </>
                            )}
                          </span>
                        </div>
                      )}
                      </div>
                    </Popup>
                  </Marker>
                )}
            </MapContainer>
          </div>

          {/* --- Tabla aperturas --- */}
          <div className="bg-white p-4 rounded shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold">Eventos recientes</h3>
                <p className="text-sm text-gray-500">Aperturas y localizaciones</p>
              </div>
              <div className="flex gap-2">
              <div className="flex gap-4 items-end">
                <div>
                  <label className="text-sm text-gray-600">Desde </label>
                  <input
                    type="datetime-local"
                    className="border p-1 rounded"
                    value={fromDateTime}
                    onChange={(e) => setFromDateTime(e.target.value)}
                  />
                </div>

                <div>
                  <label className="text-sm text-gray-600">Hasta </label>
                  <input
                    type="datetime-local"
                    className="border p-1 rounded"
                    value={toDateTime}
                    onChange={(e) => setToDateTime(e.target.value)}
                  />
                </div>
              </div>
                <button
                  className="px-3 py-1 rounded border border-green-500 text-green-700 hover:bg-green-50"
                  onClick={() => {
                    if (
                      lastLocation &&
                      isNum(lastLocation.latitude) &&
                      isNum(lastLocation.longitude)
                    ) {
                      setSelectedCoords([
                        Number(lastLocation.latitude),
                        Number(lastLocation.longitude)
                      ]);
                    }
                  }}
                >
                  Ir a última localización
                </button>
                <button
                  className={`px-3 py-1 rounded border ${
                    tableMode === "apertures"
                      ? EVENT_COLORS.Apertura.buttonActive
                      : EVENT_COLORS.Apertura.button
                  }`}
                  onClick={() => setTableMode("apertures")}
                >
                  Solo aperturas
                </button>
                <button
                  className={`px-3 py-1 rounded border ${
                    tableMode === "coords"
                      ? EVENT_COLORS.Localización.buttonActive
                      : EVENT_COLORS.Localización.button
                  }`}
                  onClick={() => setTableMode("coords")}
                >
                  Solo coordenadas
                </button>
                <button
                  className={`px-3 py-1 rounded border ${
                    tableMode === "all"
                      ? "bg-blue-600 text-white border-blue-600"
                      : "text-blue-600 border-blue-200"
                  }`}
                  onClick={() => setTableMode("all")}
                >
                  Ver todo
                </button>
                <button
                  className="px-3 py-1 rounded border border-gray-400 hover:bg-gray-100"
                  onClick={() => {
                    setFromDateTime("");
                    setToDateTime("");
                  }}
                >
                  Limpiar filtros
                </button>
              </div>
            </div>

            <div className="overflow-x-auto mt-4">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2 text-left">#</th>
                    <th className="p-2 text-left">Fecha</th>
                    <th className="p-2 text-left">Hora capturada</th>
                    <th className="p-2 text-left">Hora recibida</th>
                    <th className="p-2 text-left">Evento</th>
                    <th className="p-2 text-left">Transporte</th>
                    <th className="p-2 text-left">Usuario</th>
                    <th className="p-2 text-left">Latitud</th>
                    <th className="p-2 text-left">Longitud</th>
                    <th className="p-2 text-left">Batería</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.length ? (
                    paginatedRows.map((row) => (
                      <tr
                        key={row.id}
                        className={`
                          border-b cursor-pointer
                          ${
                            EVENT_COLORS[row.eventType]?.row ?? "hover:bg-gray-50"
                          }
                        `}
                        onClick={() => {
                          if (isNum(row.lat) && isNum(row.lng)) {
                            setSelectedCoords([Number(row.lat), Number(row.lng)]);
                          }
                        }}
                      >
                        <td className="p-2">{row.index}</td>
                        <td className="p-2">
                          {dayjs(row.capturedAt).isValid()
                            ? dayjs(row.capturedAt).format("DD/MM/YYYY")
                            : "—"}
                        </td>
                        <td className="p-2">
                          {dayjs(row.capturedAt).isValid()
                            ? dayjs(row.capturedAt).format("HH:mm:ss")
                            : "—"}
                        </td>

                        <td className="p-2">
                          {dayjs(row.uploadedAt).isValid()
                            ? dayjs(row.uploadedAt).format("HH:mm:ss")
                            : "—"}
                        </td>
                        <td className="p-2">{row.eventType}</td>
                        <td className="p-2">{row.transport ?? "—"}</td>
                        <td className="p-2">{row.user ?? "—"}</td>
                        <td className="p-2">{row.lat ?? "—"}</td>
                        <td className="p-2">{row.lng ?? "—"}</td>
                        <td className="p-2">{fmtBattery(row.battery)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} className="p-3 text-gray-500">
                        Sin datos
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {totalTablePages > 1 && (
              <div className="flex justify-end gap-2 mt-2">
                <button
                  disabled={pageTable <= 1}
                  onClick={() => setPageTable((p) => p - 1)}
                  className="px-2 py-1 border rounded disabled:opacity-50"
                >
                  Anterior
                </button>
                <button
                  disabled={pageTable >= totalTablePages}
                  onClick={() => setPageTable((p) => p + 1)}
                  className="px-2 py-1 border rounded disabled:opacity-50"
                >
                  Siguiente
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
